import React, { useState, useEffect } from 'react';
import SideMenu from '../components/SideMenu/SideMenu';
import PageHeader from '../components/PageHeader/PageHeader';
import AlertaBanner from '../components/AlertaBanner/AlertaBanner';
import useAuth from '../hooks/useAuth';
import './AppLayout.css';

/**
 * AppLayout - Layout wrapper que integra componentes fixos do sistema
 * 
 * Estrutura:
 * - SideMenu: 220px fixo à esquerda, gradiente azul-indigo
 * - PageHeader: 45px fixo no topo após SideMenu, branco com shadow (consome Context)
 * - AlertaBanner: no fluxo do documento dentro de main, desloca conteúdo quando aparece
 * - Main content: área principal com padding
 * 
 * Props:
 * - titulo: string - Título da página atual (ex: "Controle de Exaustores")
 * - cargo: string - Cargo do usuário autenticado (admin, sindico, porteiro) - OPCIONAL (pega do useAuth se não fornecido)
 * - problemas: array - Lista de alertas/problemas (opcional)
 * - children: ReactNode - Conteúdo da página
 */
const AppLayout = ({
  titulo,
  cargo: cargoProp,
  problemas = [],
  onRefresh,
  isChecking,
  children
}) => {
  const { user } = useAuth();
  const mobileMediaQuery = '(max-width: 768px)';
  
  // Usa cargo passado como prop, ou pega do usuário logado
  const cargo = cargoProp || user?.cargo || '';
  // Recupera estado do menu do localStorage (mantém estado entre navegações)
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    if (window.matchMedia(mobileMediaQuery).matches) {
      return true;
    }

    const saved = localStorage.getItem('sideMenuCollapsed');
    return saved === 'true';
  });

  // Persiste estado no localStorage quando mudar
  useEffect(() => {
    if (window.matchMedia(mobileMediaQuery).matches) {
      return;
    }

    localStorage.setItem('sideMenuCollapsed', isMenuCollapsed);
  }, [isMenuCollapsed]);

  useEffect(() => {
    const media = window.matchMedia(mobileMediaQuery);
    const handleMediaChange = (event) => {
      if (event.matches) {
        setIsMenuCollapsed(true);
      }
    };

    if (media.matches) {
      setIsMenuCollapsed(true);
    }

    media.addEventListener('change', handleMediaChange);
    return () => media.removeEventListener('change', handleMediaChange);
  }, []);

  return (
    <div className="app-layout">
      <SideMenu
        isCollapsed={isMenuCollapsed}
        onNavigate={() => {
          if (window.matchMedia(mobileMediaQuery).matches) {
            setIsMenuCollapsed(true);
          }
        }}
      />
      
      {/* Overlay para mobile - permite fechar menu ao clicar fora */}
      {!isMenuCollapsed && (
        <div 
          className="app-layout__overlay"
          onClick={() => setIsMenuCollapsed(true)}
          aria-label="Fechar menu"
        />
      )}
      
      <div className="app-layout__content">
        <PageHeader 
          titulo={titulo}
          cargo={cargo}
          isMenuCollapsed={isMenuCollapsed}
          onToggleMenu={() => setIsMenuCollapsed(!isMenuCollapsed)}
        />
        
        <AlertaBanner problemas={problemas} onRefresh={onRefresh} isChecking={isChecking} />
        
        <main className="app-layout__main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
